import {twJoin} from 'tailwind-merge'
import {useRef} from 'react'

import {twMerge} from '@sdox/utils'

import {ResponsiveMedia} from '../ResponsiveMedia'
import {RichText} from '../RichText'
import {useContainerQuery} from '../../hooks'

import type {ResponsiveMediaType} from '../ResponsiveMedia'
import type {ReactNode} from 'react'
import type {ClassNameValue} from 'tailwind-merge'

export interface HighlightProps {
  className?: ClassNameValue
  mediaPosition?: 'leading' | 'trailing'
  headline?: string // Heading is required by design but currently not by API. Design assumes there is always a headline
  media: ResponsiveMediaType
  callout?: string
  cta?: ReactNode
}

// Videos are not yet supported, since there is no design for the video dialog yet.
export const Highlight = (props: HighlightProps) => {
  const {
    callout,
    className,
    cta,
    headline,
    media,
    mediaPosition,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const isDesktop = useContainerQuery({containerRef, width: 961})

  const wrapperClassName = twMerge(
    'pullout-rounded-lg grid grid-cols-1 overflow-hidden @[961px]:grid-cols-2',
    className,
  )

  const mediaWrapperClassName = twJoin(
    'aspect-[3/2] @[961px]:aspect-[1/1]',
    mediaPosition === 'leading' && 'order-first',
    mediaPosition === 'trailing' && 'order-first @[961px]:order-last',
  )

  return (
    <div
      ref={containerRef}
      className={'@container/block'}
    >
      <div className={wrapperClassName}>
        <div className={mediaWrapperClassName}>
          <ResponsiveMedia
            media={media}
            currentScreenSize={isDesktop ? 'desktop' : 'mobile'}
            classNames={{
              image: 'size-full object-cover object-center',
            }}
          />
        </div>
        <div
          className={twJoin(
            'flex flex-col justify-center gap-spacing-20',
            'px-spacing-20 pt-spacing-44 pb-spacing-56',
            '@[610px]:p-container-large',
          )}
        >
          {headline ? (
            <RichText
              as={'h2'}
              className={'text-h2'}
              content={headline}
            />
          ) : null}
          {callout && (
            <div className={'text-p1'}>
              {callout}
            </div>
          )}
          {cta && (
            <div className={'self-start pt-spacing-8 @[610px]:pt-spacing-16'}>
              {cta}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
